import type { ReactNode } from 'react'

import type { MenuNode } from './menuTree'

/**
 * صلاحيات الصفحات — كل صفحة بالقائمة الجانبية لها صلاحيتها.
 *
 * المفاتيح المخزّنة نوعان:
 *  - مفتاح قسم (crm، hr…) = القسم كاملاً، بما يُضاف له من صفحات لاحقاً.
 *  - مسار صفحة (/crm/media-leads) = تلك الصفحة وحدها وما تحتها من شاشات (/employees/12…).
 * لوحة التحكم و«بوابتي» متاحتان للجميع، ومدير النظام يرى كل شيء.
 */
export const ALWAYS_VISIBLE = new Set(['dashboard', '/', 'my'])

/**
 * صفحات لا تُمنح لغير مدير النظام — فارغة اليوم: كل صفحة بالقائمة تُمنح من محرّر الصلاحيات
 * («خريطة الفريق» صارت قابلة للمنح منذ 2026-10-17، والخادم يحصرها بأدوار المدراء).
 */
export const ADMIN_ONLY_PAGES = new Set<string>()

/**
 * أقسامٌ يحصرها الدور فوق المنح: لا تظهر ولا تُفتح إلا لمن يحمل أحد هذه الأدوار (ومدير النظام دائماً)
 * حتى لو مُنح القسم أو صفحاته — «نظام محاسبي» للمحاسب والمدير وحدهما (منذ 2026-10-26).
 * المرجع بالخادم: PagePermissions.SectionRoles — يُحدَّث الاثنان معاً.
 */
export const SECTION_ROLES: Record<string, readonly string[]> = {
  accounting: ['Accountant'],
}

/** هل يحمل المستخدم دور القسم (إن كان محصوراً بدور)؟ */
export const sectionRoleAllows = (section: string, roles: readonly string[] = []): boolean => {
  const need = SECTION_ROLES[section]
  return !need || roles.includes('Admin') || need.some((r) => roles.includes(r))
}

export interface CatalogPage {
  key: string
  label: ReactNode
  icon?: ReactNode
}
export interface CatalogGroup {
  /** عنوان المجموعة الفرعية (الحسابات، الفواتير…) — بلا عنوان للصفحات المباشرة */
  label?: ReactNode
  icon?: ReactNode
  pages: CatalogPage[]
}
export interface CatalogSection {
  key: string
  label: ReactNode
  icon?: ReactNode
  groups: CatalogGroup[]
  pages: string[]
  /** الأدوار التي يُحصر بها القسم فوق المنح (المحرّر ينبّه بها) */
  roles?: readonly string[]
}

const isPage = (n: MenuNode) => typeof n.key === 'string' && n.key.startsWith('/')

/** كتالوج الصفحات القابلة للمنح، مجمّعاً بالأقسام والمجموعات الفرعية */
export function buildCatalog(tree: MenuNode[]): CatalogSection[] {
  const sections: CatalogSection[] = []
  for (const s of tree) {
    if (!s.key || !s.children || ALWAYS_VISIBLE.has(s.key)) continue
    const groups: CatalogGroup[] = []
    const direct: CatalogPage[] = []
    for (const c of s.children) {
      if (c.type === 'divider') continue
      if (isPage(c)) {
        if (!ADMIN_ONLY_PAGES.has(c.key!))
          direct.push({ key: c.key!, label: c.label, icon: c.icon })
      } else if (c.children) {
        const pages = c.children
          .filter((p) => p.type !== 'divider' && isPage(p) && !ADMIN_ONLY_PAGES.has(p.key!))
          .map((p) => ({ key: p.key!, label: p.label, icon: p.icon }))
        if (pages.length) groups.push({ label: c.label, icon: c.icon, pages })
      }
    }
    if (direct.length) groups.unshift({ pages: direct })
    const pages = groups.flatMap((g) => g.pages.map((p) => p.key))
    if (pages.length)
      sections.push({
        key: s.key,
        label: s.label,
        icon: s.icon,
        groups,
        pages,
        roles: SECTION_ROLES[s.key],
      })
  }
  return sections
}

/** شاشاتٌ ليست بالقائمة تتبع صلاحية الصفحة التي تُفتح منها */
const PATH_ALIASES: Array<[RegExp, string]> = [
  // الإدارة المالية لمجمعٍ بعينه ← «الإدارة المالية»
  [/^\/crm\/residential-complexes\/\d+\/finance$/, '/crm/residential-complexes/finance'],
  // قائمة الزبائن تُعرض داخل «تقرير الكول سنتر بالتفاصيل»
  [/^\/crm\/customers(\/|$)/, '/crm/callcenter-report'],
  // سجل الكتب وتعديل كتابٍ ← «الكتب»
  [/^\/crm\/books(\/\d+)?$/, '/crm/books/new'],
  // البنرات القديمة ← «أقسام الرئيسية»
  [/^\/marketing\/banners(\/|$)/, '/marketing/home-sections'],
]

/** المسار الذي تُفحص صلاحيته (بعد تحويل الشاشات الملحقة إلى صفحاتها) */
export function permissionPathFor(path: string): string {
  for (const [re, page] of PATH_ALIASES) if (re.test(path)) return page
  return path
}

/** الصفحة (وقسمها) التي يقع تحتها المسار — أطول مطابقة بحدود المقاطع */
export function locatePage(
  tree: MenuNode[],
  path: string,
): { section: string; page: string } | null {
  const target = permissionPathFor(path)
  let best: { section: string; page: string } | null = null
  const visit = (section: string, nodes: MenuNode[]) => {
    for (const n of nodes) {
      if (n.children) visit(section, n.children)
      else if (isPage(n)) {
        const k = n.key!
        if ((target === k || target.startsWith(k + '/')) && (!best || k.length > best.page.length))
          best = { section, page: k }
      }
    }
  }
  for (const s of tree) if (s.key && s.children) visit(s.key, s.children)
  return best
}

/**
 * هل يفتح المستخدم هذا المسار؟ المسارات خارج القائمة (نماذج فرعية مثلاً) لا يقيّدها هذا الحارس —
 * تبقى أدوار الـ API هي الحارس الأساسي، وهذا دفاعٌ بالعمق يمنع الوصول المباشر بالرابط.
 */
export function canOpenPath(
  tree: MenuNode[],
  path: string,
  perms: ReadonlySet<string>,
  isAdmin: boolean,
  roles: readonly string[] = [],
): boolean {
  if (isAdmin) return true
  const hit = locatePage(tree, path)
  if (!hit) return true
  if (ALWAYS_VISIBLE.has(hit.section)) return true
  if (!sectionRoleAllows(hit.section, roles)) return false
  if (ADMIN_ONLY_PAGES.has(hit.page)) return false
  return perms.has(hit.section) || perms.has(hit.page)
}

/** يحذف الفواصل الزائدة (بالبداية والنهاية والمتتالية) بعد تصفية الصفحات */
function tidyDividers(nodes: MenuNode[]): MenuNode[] {
  const out: MenuNode[] = []
  for (const n of nodes) {
    if (n.type === 'divider' && (out.length === 0 || out[out.length - 1].type === 'divider'))
      continue
    out.push(n)
  }
  while (out.length && out[out.length - 1].type === 'divider') out.pop()
  return out
}

/** القائمة كما يراها المستخدم: القسم كاملاً بمفتاحه، وإلا صفحاته الممنوحة فقط */
export function filterMenuTree(
  tree: MenuNode[],
  perms: ReadonlySet<string>,
  isAdmin: boolean,
  roles: readonly string[] = [],
): MenuNode[] {
  if (isAdmin) return tree
  const keep = (nodes: MenuNode[]): MenuNode[] =>
    tidyDividers(
      nodes.flatMap((n): MenuNode[] => {
        if (n.type === 'divider') return [n]
        if (isPage(n)) return perms.has(n.key!) && !ADMIN_ONLY_PAGES.has(n.key!) ? [n] : []
        if (n.children) {
          const kids = keep(n.children)
          return kids.some((k) => k.type !== 'divider') ? [{ ...n, children: kids }] : []
        }
        return []
      }),
    )
  return tree.flatMap((s): MenuNode[] => {
    if (!s.key) return []
    if (ALWAYS_VISIBLE.has(s.key)) return [s]
    // القسم المحصور بدور لا يظهر بلا دوره — مهما مُنح
    if (!sectionRoleAllows(s.key, roles)) return []
    if (perms.has(s.key)) return [s]
    if (!s.children) return []
    const kids = keep(s.children)
    return kids.length ? [{ ...s, children: kids }] : []
  })
}

/** المفاتيح المخزّنة ← مجموعة الصفحات المحدّدة (مفتاح القسم = كل صفحاته) */
export function expandPermissions(keys: readonly string[], catalog: CatalogSection[]): Set<string> {
  const out = new Set<string>()
  for (const k of keys) {
    const section = catalog.find((s) => s.key === k)
    if (section) section.pages.forEach((p) => out.add(p))
    else if (k.startsWith('/')) out.add(k)
  }
  return out
}

/**
 * الصفحات المحدّدة ← المفاتيح التي تُخزَّن: قسمٌ حُدّدت كل صفحاته يُحفظ بمفتاحه (فيشمل صفحاته
 * المستقبلية)، وإلا تُحفظ صفحاته المحدّدة واحدةً واحدة.
 */
export function compressPermissions(
  pages: ReadonlySet<string>,
  catalog: CatalogSection[],
): string[] {
  const out: string[] = []
  for (const s of catalog) {
    const picked = s.pages.filter((p) => pages.has(p))
    if (picked.length === 0) continue
    if (picked.length === s.pages.length) out.push(s.key)
    else out.push(...picked)
  }
  return out
}

export interface PermissionSummary {
  pages: number
  total: number
  sections: Array<{ key: string; label: ReactNode; granted: number; of: number }>
}

/** ملخّص صلاحيات مستخدم: عدد صفحاته، وما مُنح من كل قسم (للجدول والتلميحات) */
export function summarizePermissions(
  keys: readonly string[],
  catalog: CatalogSection[],
): PermissionSummary {
  const pages = expandPermissions(keys, catalog)
  const sections = catalog
    .map((s) => ({
      key: s.key,
      label: s.label,
      granted: s.pages.filter((p) => pages.has(p)).length,
      of: s.pages.length,
    }))
    .filter((s) => s.granted > 0)
  return {
    pages: sections.reduce((n, s) => n + s.granted, 0),
    total: catalog.reduce((n, s) => n + s.pages.length, 0),
    sections,
  }
}
